import { useMutation, useQueryClient } from '@tanstack/react-query';
import config from 'src/config';
import { useToast } from 'src/context/ToastContext';
import ApiError from '../ApiError';
import { buildPosthogHeaders } from 'src/api/posthogHeaders';

export const useDeleteRebateAgreement = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<void, ApiError, string>({
    mutationFn: async (agreementId: string) => {
      const response = await fetch(
        `${config.apiBaseUrl}/rebates/agreements/${agreementId}`,
        {
          method: 'DELETE',
          headers: buildPosthogHeaders({ 'Content-Type': 'application/json' }),
          credentials: 'include',
        },
      );

      if (!response.ok) {
        const errorResponse: { status?: number; message?: string } =
          await response.json().catch(() => ({}));
        throw new ApiError(
          errorResponse.status || response.status,
          errorResponse.message || 'Failed to delete rebate agreement',
        );
      }
    },
    onSuccess: () => {
      showToast({
        message: 'Rebate agreement deleted successfully',
        title: 'Success',
        color: 'success',
        icon: 'check',
      });
      queryClient.invalidateQueries({ queryKey: ['rebateAgreements'] });
      queryClient.invalidateQueries({ queryKey: ['rebateAgreement'] });
    },
    onError: (error) => {
      showToast({
        message:
          error instanceof ApiError
            ? error.message
            : 'Failed to delete rebate agreement',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
};

export default useDeleteRebateAgreement;
